import { apiFunctionsEnum } from 'constants/api.constants';
import ApiService from './api.service';
import {
	setCustomerDashboard,
	setBeneficiaries,
	setMoneyActivities,
	setBankAccounts,
	setSupportRequests,
	setBalances,
	setBeneficiaryPayments,
	setGridTab,
	setCloseAccountVerificationResult,
	setError,
	setIsLoading,
	setSelectedEntity,
} from 'state/reducers/customer-accounts.reducers';
import { buildPaginationQueryParams, setErrorMessage, IPaginationParams } from 'utils/index';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { formatDateTime } from 'constants/global.constants';
import { store } from 'state/store';
import { selectTranslations } from 'state/reducers/translation.reducers';

const HYPER_SYNC_TRANSLATION_PREFIX = 'page.customerAccounts.forms.beneficiaries.hyperSync';

const toHyperSyncOperationTypeKey = (operationType: string) => operationType.replace(/\./g, '_');

/** Maps sync-state API envelope to FormCreator display fields on the beneficiary record. */
export function mapBeneficiaryHyperSyncFormFields(syncEnvelope: any) {
	const t = selectTranslations(store.getState());
	if (syncEnvelope === null) {
		return {
			hyperSyncStatus: t(`${HYPER_SYNC_TRANSLATION_PREFIX}.loadFailed`),
			hyperSyncLastAction: '',
			hyperSyncLastAttemptAt: '',
		};
	}
	const syncState = syncEnvelope.syncState;
	if (!syncState) {
		return {
			hyperSyncStatus: t(`${HYPER_SYNC_TRANSLATION_PREFIX}.none`),
			hyperSyncLastAction: '',
			hyperSyncLastAttemptAt: '',
		};
	}
	return {
		hyperSyncStatus: t(`${HYPER_SYNC_TRANSLATION_PREFIX}.status.${syncState.lastAttemptStatus}`),
		hyperSyncLastAction: t(
			`${HYPER_SYNC_TRANSLATION_PREFIX}.operationType.${toHyperSyncOperationTypeKey(
				syncState.lastAttemptOperationType
			)}`
		),
		hyperSyncLastAttemptAt: formatDateTime(syncState.lastAttemptAt),
	};
}

export function getCustomerDashboard(id: number) {
	return async (dispatch: any) => {
		dispatch(setIsLoading(true));
		dispatch(setCustomerDashboard(null));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_CUSTOMER_BY_ID, { id })
			.then((response: any) => {
				const {
					basicData,
					plan,
					pendingRequests,
					nextMonthPlan,
					balances,
					lastCompletedPayment,
					contacts,
					bankAccounts,
				} = response.data;
				dispatch(
					setCustomerDashboard({
						basicData,
						plan,
						pendingRequests,
						nextMonthPlan,
						balances,
						lastCompletedPayment,
						contacts,
						bankAccounts,
					})
				);
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				dispatch(setCustomerDashboard(null));
			})
			.finally(() => {
				dispatch(setIsLoading(false));
			});
	};
}

export function verifyCloseAccount(companyId: number) {
	return async (dispatch: any) => {
		dispatch(setIsLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.VERIFY_CLOSE_ACCOUNT, { companyId })
			.then((response: any) => {
				const { isCompanyHasPendingFinancialRequest, isCompanyHasBalance, contactsWithPermissions } =
					response.data;
				dispatch(
					setCloseAccountVerificationResult({
						isCompanyHasPendingFinancialRequest,
						isCompanyHasBalance,
						contactsWithPermissions,
					})
				);
			})
			.catch((error: any) => {
				dispatch(setCloseAccountVerificationResult(null));
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setIsLoading(false));
			});
	};
}

export const verifyCloseAccountInline = createAsyncThunk('fetchData', (companyId: number, { rejectWithValue }) =>
	ApiService.getInstance()
		.execute(apiFunctionsEnum.VERIFY_CLOSE_ACCOUNT, { companyId })
		.then((response) => {
			return response.data;
		})
		.catch((err) => rejectWithValue(err))
);

export function closeAccount(companyId: number, closeRequestByContactId: number, closeReason: string) {
	return async (dispatch: any) => {
		dispatch(setIsLoading(true));
		dispatch(setCloseAccountVerificationResult(null));
		dispatch(setGridTab(0));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.CLOSE_ACCOUNT, { companyId, closeRequestByContactId, closeReason })
			.then(() => {
				dispatch(setCustomerDashboard(null));
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setIsLoading(false));
			});
	};
}

export function resendWelcomeEmail(contactIds: any) {
	return async (dispatch: any) => {
		dispatch(setIsLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.RESEND_WELCOME_EMAIL, { contactIds })
			.then((response: any) => {
				return response;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setIsLoading(false));
			});
	};
}

export function getCompanyBeneficiaries(companyId: number, params?: IPaginationParams) {
	return async (dispatch: any) => {
		const queryParams = buildPaginationQueryParams(params, { sortBy: 'created_at' });
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_BENEFICIARIES, { id: companyId, ...queryParams })
			.then((response: any) => {
				dispatch(
					setBeneficiaries({
						data: response.data.result,
						pagination: response.data.pagination,
					})
				);
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			});
	};
}

export function getCompanyActivities(companyId: number, params?: IPaginationParams) {
	return async (dispatch: any) => {
		const queryParams = buildPaginationQueryParams(params, { sortBy: 'created_at' });
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_ACTIVITIES, { id: companyId, ...queryParams })
			.then((response: any) => {
				dispatch(
					setMoneyActivities({
						data: response.data.result,
						pagination: response.data.pagination,
					})
				);
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			});
	};
}

export function getCompanyBankAccounts(companyId: number, params?: IPaginationParams) {
	return async (dispatch: any) => {
		const queryParams = buildPaginationQueryParams(params, { sortBy: 'created_at' });
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_BANK_ACCOUNTS, { id: companyId, ...queryParams })
			.then((response: any) => {
				dispatch(
					setBankAccounts({
						data: response.data.result,
						pagination: response.data.pagination,
					})
				);
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			});
	};
}

export function getCompanySupportRequests(companyId: number, params?: IPaginationParams) {
	return async (dispatch: any) => {
		const queryParams = buildPaginationQueryParams(params, { sortBy: 'created_at' });
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_SUPPORT_REQUESTS, { id: companyId, ...queryParams })
			.then((response: any) => {
				dispatch(
					setSupportRequests({
						data: response.data.result,
						pagination: response.data.pagination,
					})
				);
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			});
	};
}

export function getCompanyBalances(companyId: number, params?: IPaginationParams) {
	return async (dispatch: any) => {
		const queryParams = buildPaginationQueryParams(params, { sortBy: 'created_at' });
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_BALANCES, { id: companyId, ...queryParams })
			.then((response: any) => {
				dispatch(
					setBalances({
						data: response.data.result,
						pagination: response.data.pagination,
					})
				);
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			});
	};
}

export function getCompanyContactById(companyId: number, contactId: number) {
	return async (dispatch: any) => {
		dispatch(setIsLoading(true));
		dispatch(setSelectedEntity(null)); // Clear previous
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_CONTACT_BY_ID, { id: companyId, contactId })
			.then((response: any) => {
				dispatch(setSelectedEntity({ type: 'contacts', data: response.data.contact }));
				return response.data.contact;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return null;
			})
			.finally(() => {
				dispatch(setIsLoading(false));
			});
	};
}

export function getCompanyBeneficiaryById(companyId: number, beneficiaryId: number) {
	return async (dispatch: any) => {
		dispatch(setIsLoading(true));
		dispatch(setSelectedEntity(null));
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_BENEFICIARY_BY_ID, { id: companyId, beneficiaryId })
			.then(async (response: any) => {
				const syncEnvelope = await fetchCompanyBeneficiaryHyperSyncState(companyId, beneficiaryId);
				const data = {
					...response.data.beneficiary,
					...mapBeneficiaryHyperSyncFormFields(syncEnvelope),
				};
				dispatch(setSelectedEntity({ type: 'beneficiaries', data }));
				return data;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return null;
			})
			.finally(() => {
				dispatch(setIsLoading(false));
			});
	};
}

/**
 * Fetch paginated payments for a specific beneficiary (תשלומים למוטב)
 * Dispatches to Redux for BeneficiaryPaymentsTable
 */
export function getCompanyBeneficiaryPayments(companyId: number, beneficiaryId: number, params?: IPaginationParams) {
	return async (dispatch: any) => {
		const queryParams = buildPaginationQueryParams(params, { sortBy: 'created_at' });
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_BENEFICIARY_PAYMENTS, {
				id: companyId,
				beneficiaryId,
				...queryParams,
			})
			.then((response: any) => {
				dispatch(
					setBeneficiaryPayments({
						data: response.data.result,
						pagination: response.data.pagination,
					})
				);
				return response.data;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return null;
			});
	};
}

export function fetchCompanyBeneficiaryHyperSyncState(companyId: number, beneficiaryId: number) {
	return ApiService.getInstance()
		.execute(apiFunctionsEnum.GET_COMPANY_BENEFICIARY_HYPER_SYNC_STATE, {
			id: companyId,
			beneficiaryId,
		})
		.then((response: any) => response.data)
		.catch(() => null);
}

export function getCompanyBankAccountById(companyId: number, bankAccountId: number) {
	return async (dispatch: any) => {
		dispatch(setIsLoading(true));
		dispatch(setSelectedEntity(null));
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_BANK_ACCOUNT_BY_ID, { id: companyId, bankAccountId })
			.then((response: any) => {
				dispatch(setSelectedEntity({ type: 'bankAccounts', data: response.data.bankAccount }));
				return response.data.bankAccount;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return null;
			})
			.finally(() => {
				dispatch(setIsLoading(false));
			});
	};
}

export function getCompanyPaymentById(companyId: number, paymentId: number) {
	return async (dispatch: any) => {
		dispatch(setIsLoading(true));
		dispatch(setSelectedEntity(null));
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_PAYMENT_BY_ID, { id: companyId, paymentId })
			.then((response: any) => {
				dispatch(setSelectedEntity({ type: 'payment', data: response.data.payment }));
				return response.data.payment;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return null;
			})
			.finally(() => {
				dispatch(setIsLoading(false));
			});
	};
}

export function getCompanyConversionById(companyId: number, conversionId: number) {
	return async (dispatch: any) => {
		dispatch(setIsLoading(true));
		dispatch(setSelectedEntity(null));
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_CONVERSION_BY_ID, { id: companyId, conversionId })
			.then((response: any) => {
				dispatch(setSelectedEntity({ type: 'conversion', data: response.data.conversion }));
				return response.data.conversion;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return null;
			})
			.finally(() => {
				dispatch(setIsLoading(false));
			});
	};
}

export function getCompanyBalanceById(companyId: number, balanceId: number) {
	return async (dispatch: any) => {
		dispatch(setIsLoading(true));
		dispatch(setSelectedEntity(null));
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_BALANCE_BY_ID, { id: companyId, balanceId })
			.then((response: any) => {
				dispatch(setSelectedEntity({ type: 'balance', data: response.data.balance }));
				return response.data.balance;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return null;
			})
			.finally(() => {
				dispatch(setIsLoading(false));
			});
	};
}

export function getCompanyBalanceRefundById(companyId: number, refundId: number) {
	return async (dispatch: any) => {
		dispatch(setIsLoading(true));
		dispatch(setSelectedEntity(null));
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPANY_BALANCE_REFUND_BY_ID, { id: companyId, refundId })
			.then((response: any) => {
				dispatch(setSelectedEntity({ type: 'balanceRefund', data: response.data.balanceRefund }));
				return response.data.balanceRefund;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return null;
			})
			.finally(() => {
				dispatch(setIsLoading(false));
			});
	};
}
